export const CUTTING_POINT_MIN_GAP_MS = 50;
export const MARQUEE_HIT_TOLERANCE_MS = 5;
export const SLOW_DRAG_FACTOR = 0.2;
export const MIN_ZOOM = 1;
export const MAX_ZOOM = 64;
export const ZOOM_STEP = 8;
export const DEFAULT_BPM = 120;
export const MIN_BPM = 1;
export const MAX_BPM = 300;
export const MIN_TICK_GAP_PX = 6;
export const MAX_CUTTING_POINT_GAP_MS = 3999;

export const CUTTING_POINT_GAP_ERROR_MESSAGE = `Cutting points can be at most ${(
  MAX_CUTTING_POINT_GAP_MS / 1000
).toFixed(1)}s apart`;

// Alt to signal that clicking will remove the cutting point.
export const TRASH_CURSOR =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='-2 -2 28 28' fill='none' stroke='%23ffffff' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cdefs%3E%3Cfilter id='s' x='-50%25' y='-50%25' width='200%25' height='200%25'%3E%3CfeDropShadow dx='0' dy='0' stdDeviation='1' flood-color='%23000000' flood-opacity='0.3'/%3E%3C/filter%3E%3C/defs%3E%3Cg filter='url(%23s)'%3E%3Cpath d='M4 7l16 0'/%3E%3Cpath d='M10 11l0 6'/%3E%3Cpath d='M14 11l0 6'/%3E%3Cpath d='M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12'/%3E%3Cpath d='M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3'/%3E%3C/g%3E%3C/svg%3E\") 14 14, auto";

export const SHORTCUTS: { keys: string; action: string }[] = [
  { keys: "Alt-click handle", action: "Remove a cutting point" },
  { keys: "Shift-click handle", action: "Add to the selection" },
  { keys: "Drag waveform", action: "Select a range of points" },
  { keys: "Cmd/Ctrl + drag", action: "Finer control while dragging" },
  { keys: ", / .", action: "Jump to the previous / next point" },
  { keys: "F", action: "Add a cutting point at the playhead" },
  { keys: "Cmd/Ctrl + Z", action: "Undo" },
  { keys: "Cmd/Ctrl + Shift + Z", action: "Redo" },
  { keys: "Cmd/Ctrl + C", action: "Copy selected points" },
  { keys: "Cmd/Ctrl + V", action: "Paste at the playhead" },
  { keys: "Cmd/Ctrl + D", action: "Duplicate selected points" },
  { keys: "Cmd/Ctrl + Backspace", action: "Delete selected points" },
  { keys: "Cmd/Ctrl + S", action: "Toggle snap to grid" },
  { keys: "Cmd/Ctrl + H", action: "Toggle the grid" },
  { keys: "Cmd/Ctrl + L", action: "Toggle the gap labels" },
  { keys: "Cmd/Ctrl + Shift + G", action: "Align the grid to the playhead" },
];

export function formatMs(ms: number): string {
  const totalSeconds = ms / 1000;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toFixed(1).padStart(4, "0")}`;
}

export function validateCuttingPointGaps(cuttingPoints: number[]): boolean {
  let previousMs = 0;

  for (const cuttingPoint of [...cuttingPoints].sort((a, b) => a - b)) {
    if (cuttingPoint - previousMs > MAX_CUTTING_POINT_GAP_MS) {
      return false;
    }

    previousMs = cuttingPoint;
  }

  return true;
}

export function pointsEqual(a: number[], b: number[]): boolean {
  return a.length === b.length && a.every((value, i) => value === b[i]);
}

export function snapToBeat(
  ms: number,
  {
    enableGrid,
    snapToGrid,
    bpm,
    gridOffsetMs,
    songDurationMs,
  }: {
    enableGrid: boolean;
    snapToGrid: boolean;
    bpm: number;
    gridOffsetMs: number;
    songDurationMs: number | null;
  },
): number {
  if (!enableGrid || !snapToGrid || bpm <= 0) return ms;
  const beatMs = 60000 / bpm;
  const index = Math.round((ms - gridOffsetMs) / beatMs);
  const snapped = gridOffsetMs + index * beatMs;
  const max = songDurationMs ?? snapped;
  return Math.max(0, Math.min(max, snapped));
}

// Vertical beat ticks from the BPM. Every fourth beat is emphasised as a bar
// line. Positions are percentages of the track so they follow the zoom. When
// zoomed out far enough that ticks would overlap, only every Nth beat is drawn
// (N is a power of two so bar lines stay aligned).
export function computeBeatGridLines(
  songDurationMs: number | null,
  bpm: number,
  gridOffsetMs: number,
  waveformWidthPx: number,
): { pct: number; isBar: boolean }[] {
  const lines: { pct: number; isBar: boolean }[] = [];
  if (songDurationMs === null || bpm <= 0) return lines;

  const beatMs = 60000 / bpm;
  let step = 1;
  if (waveformWidthPx > 0) {
    const beatGapPx = (beatMs / songDurationMs) * waveformWidthPx;
    while (beatGapPx * step < MIN_TICK_GAP_PX) step *= 2;
  }
  // Shift the grid by the offset, keeping the bar phase aligned to it. The
  // first beat index may be negative, so the modulo checks are normalised.
  const firstIndex = Math.ceil(-gridOffsetMs / beatMs);
  for (
    let i = firstIndex, t = gridOffsetMs + firstIndex * beatMs;
    t <= songDurationMs;
    i++, t += beatMs
  ) {
    if (t < 0) continue;
    if (((i % step) + step) % step !== 0) continue;
    lines.push({
      pct: (t / songDurationMs) * 100,
      isBar: ((i % 4) + 4) % 4 === 0,
    });
  }
  return lines;
}

// Normalise an offset (ms) to a single bar so the stored grid offset stays
// small. Shared by beat detection and the "align grid to playhead" action.
export function normalizeGridOffsetMs(ms: number, bpm: number): number {
  const barMs = (60000 / bpm) * 4;
  return Math.round(((ms % barMs) + barMs) % barMs);
}
